import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  SUPPORTED_LANGUAGES, 
  DEFAULT_LANGUAGE, 
  getLangFromPath, 
  getNormalizedPath, 
  buildLangPath, 
  Language 
} from './languages';
import { TRANSLATIONS, TranslationKeys } from './translations';

interface I18nContextType {
  lang: string;
  currentLanguage: Language;
  supportedLanguages: Language[];
  setLanguage: (newLangCode: string) => void;
  t: (key: keyof TranslationKeys | string, fallback?: string) => string;
  getLangUrl: (route: string) => string;
  normalizedRoute: string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [normalizedRoute, setNormalizedRoute] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return window.location.pathname || '/';
    }
    return '/';
  });

  useEffect(() => {
    const handleLocationChange = () => {
      const normPath = window.location.pathname || '/';
      setNormalizedRoute(normPath);
    };

    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      handleLocationChange();
    };

    const originalReplaceState = window.history.replaceState;
    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      handleLocationChange();
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  const setLanguage = (newLangCode: string) => {
    // No-op since we only have English
  };

  const getLangUrl = (route: string): string => {
    return route;
  };

  const t = (key: keyof TranslationKeys | string, fallback?: string): string => {
    const defaultDict = TRANSLATIONS[DEFAULT_LANGUAGE];
    if (defaultDict && (defaultDict as any)[key]) {
      return (defaultDict as any)[key]!;
    }
    return fallback || String(key);
  };

  const currentLanguage = SUPPORTED_LANGUAGES[0];

  return (
    <I18nContext.Provider
      value={{
        lang: DEFAULT_LANGUAGE,
        currentLanguage,
        supportedLanguages: SUPPORTED_LANGUAGES,
        setLanguage,
        t,
        getLangUrl,
        normalizedRoute,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
